import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { TylerMatTableDataSource } from '../../../../shared-components/angular-material/extended.class';
import { DataQueryFieldFilter } from '../../models/data-query-field-filter.model';
import { DataQueryService } from '../../data-query.service';
import { DataQueryField } from '../../models/data-query-field.model';
import { LoginService } from '../../../../shared-modules/login/login.service';
import { MenuItem } from '../../../../shared-components/controls/menus/menu-item.model';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { GroupConditionInfoDialogComponent } from '../group-condition-info-dialog/group-condition-info-dialog.component';

@Component({
    selector: 'data-query-filter-table',
    templateUrl: './data-query-filter-table.component.html',
    styleUrls: ['./data-query-filter-table.component.css']
})
export class DataQueryFilterTableComponent implements OnInit, OnDestroy {

    // Interface Properties
    @Input() forStudentSearch = false;
    @Input() forCourseSearch = false;
    @Input() forStaffSearch = false;
    @Input() queryFilterFields: DataQueryField[] = [];
    @Output() filtersChange = new EventEmitter<DataQueryFieldFilter[]>();

    // Properties
    private _filters: DataQueryFieldFilter[] = [];
    V10: boolean;
    subscriptions: Subscription[] = [];
    @ViewChild('validationTrigger', { read: ElementRef, static: true }) validationTrigger: ElementRef;

    // Table Properties
    tableData = new TylerMatTableDataSource();
    columns = [
        'field',
        'operator',
        'value',
        'apply-to-all-groups',
        'group',
        'actions'
    ];

    // Class Functions
    constructor(
        private dataQueryService: DataQueryService,
        private loginService: LoginService,
        private changeDetector: ChangeDetectorRef,
        private dialog: MatDialog
    ) { }

    get filters(): DataQueryFieldFilter[] {
        return this._filters;
    }

    @Input() set filters(value: DataQueryFieldFilter[]) {
        this._filters = value;
        this.tableData.data = value.filter(x => !x.hidden);
    }

    // Event Functions
    ngOnInit() {

        // Resetting the query (because we want to, I guess???)

        // Setting initial properties
        this.V10 = this.loginService.LoginSettings.V10;
        if (this.forStudentSearch || this.forCourseSearch || this.forStaffSearch) {
            this.columns.unshift('selector');
            this.columns.splice(this.columns.indexOf('apply-to-all-groups'), 1);
        }

        // Triggering validation for initial errors
        this.validationTrigger.nativeElement.click();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    menuItemClick(item: MenuItem) {
        // PRE: an item that contains the filter that was selected and what option was selected
        // POST: performs the proper action

        // Ensuring we have a filter
        const filter: DataQueryFieldFilter = item.data;
        if (!filter) {
            return;
        }

        // Performing the proper action
        switch (item.itemName) {

            // Duplicating the field
            case 'Duplicate':

                // Creating the new filter
                const newFilter = new DataQueryFieldFilter();
                Object.assign(newFilter, filter);
                if (filter.field && filter.field.Filters) {
                    filter.field.Filters.push(newFilter);
                }
                newFilter.FilterID = DataQueryService.filterFieldId;
                DataQueryService.filterFieldId += 1;

                // Adding the filter
                const indexOfFilter = this.filters.indexOf(filter);
                this.filters.splice(indexOfFilter, 0, newFilter);
                this.tableData.data = this.filters;
                break;

            // Deleting it
            case 'Delete':
                if (this.V10 == false) {
                    if (this.queryFilterFields != undefined && this.queryFilterFields.length > 0) {
                        this.queryFilterFields.forEach(queryFilterField => {
                            if (queryFilterField.FieldName == 'StudentsActivelyEnrolledTodayInClass') {
                                queryFilterField.Filters.forEach(queryFilter => {
                                    if (queryFilter.Value2.toString() == item.data.Value2.toString()) {
                                        const siteSections = this.dataQueryService.currentQuery.siteSectionId.toString().split(',');
                                        const idx = _.indexOf(siteSections, item.data.Value2.toString());
                                        if (idx > -1) {
                                            siteSections.splice(idx, 1);
                                        }
                                        this.dataQueryService.currentQuery.siteSectionId = '';
                                        siteSections.forEach(siteSection => {
                                            this.dataQueryService.currentQuery.siteSectionId = this.dataQueryService.currentQuery.siteSectionId + (this.dataQueryService.currentQuery.siteSectionId.toString().length == 0 ? '' : ',') + siteSection;
                                        });
                                    }
                                });
                            }
                        });
                    }
                }

                filter.delete(
                    this.dialog,
                    this.filters,
                    this.queryFilterFields,
                    false,
                    this.tableData,
                    () => {
                        this.dataQueryService.updateFilter();
                    }
                );
                break;

            // Expanding the filter
            case 'Expand':
                // TODO
                break;

            // Default just does nothing (maybe we should show an error here?)
            default:
                break;
        }

        // Updating the filter translation and the table
        this.changeDetector.detectChanges();	// We do this to prevent an expression changed error
        this.filtersChange.emit(this.filters);
        this.dataQueryService.updateFilter();
        this.dataQueryService.currentQuery.queryChanged = true;
    }

    // Local Methods
    disableOptions(filter: DataQueryFieldFilter, isOperator: boolean) {
        // PRE: a field to check if the options can be edits
        // POST: returns true if the dropdowns should be disabled
        return filter.field.FieldName == DataQueryFieldFilter.courseFilterFieldName
            || (this.V10 == true && isOperator == true && filter.field.FieldName == DataQueryFieldFilter.enrollmentFilterFieldName)
            || (this.V10 == false && isOperator == true && filter.field.FieldName == DataQueryFieldFilter.enrollmentFilterFieldName)
            || filter.IsStudentFilter
            || filter.IsCourseFilter
            || filter.disableEdit
            || filter.field.FieldName == DataQueryFieldFilter.studentListFilterFieldName
            || filter.field.FieldName == DataQueryFieldFilter.studentTeamFilterFieldName;
    }

    setCurrentDate1(filter: DataQueryFieldFilter) {
        filter.dateValue = new Date();
    }

    setCurrentDate2(filter: DataQueryFieldFilter) {
        filter.dateValue2 = new Date();
    }

    // Service Methods
    checkDisabledField(field: DataQueryField) {
        return this.dataQueryService.checkDisabledField(field);
    }

    getStudentDropdownValue(value: string): string {
        return decodeURI(value);
    }

    updateFilter(filter: DataQueryFieldFilter, filterField: string, updatedValue: any, field = null) {
        if (filter[filterField] != updatedValue) {
            if (updatedValue) {
                if (this.dataQueryService.isStudentSearch) {
                    filter.UseFilterForStudentSearch = true;
                    filter.UseFilterForCourseSearch = false;
                    filter.UseFilterForStaffSearch = false;
                } else if (this.dataQueryService.isCourseSearch) {
                    filter.UseFilterForStudentSearch = false;
                    filter.UseFilterForCourseSearch = true;
                    filter.UseFilterForStaffSearch = false;
                } else if (this.dataQueryService.isStaffSearch) {
                    filter.UseFilterForStudentSearch = false;
                    filter.UseFilterForCourseSearch = false;
                    filter.UseFilterForStaffSearch = true;
                }
            }
            filter[filterField] = updatedValue;
            this.dataQueryService.updateFilter(filter, field);
            this.dataQueryService.currentQuery.queryChanged = true;
        }

        if (filter.field.Alias == 'Enrollment Date' && (updatedValue == 'Is Not Blank' || updatedValue == 'Is Blank')) {
            filter.dateValue = null;
            filter.dateValue2 = null;
            filter.UseCurrentDate1 = false;
            filter.UseCurrentDate2 = false;
        }

        if (filter.field.Alias == 'Birth Date' && (updatedValue == 'Is Not Blank' || updatedValue == 'Is Blank')) {
            filter.dateValue = null;
            filter.dateValue2 = null;
            filter.UseCurrentDate1 = false;
            filter.UseCurrentDate2 = false;
        }
    }

    openGroupMsgClick() {
        this.dialog.open(GroupConditionInfoDialogComponent, {
            disableClose: true
        });
    }
}
